import React from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';
import type { SiteTemplate } from '../../schemas/validation';
import { cn } from '../../utils/cn';

interface DeleteTemplateModalProps {
  template: SiteTemplate;
  isSubmitting: boolean;
  onConfirm: (templateId: string) => void;
  onCancel: () => void;
}

export const DeleteTemplateModal: React.FC<DeleteTemplateModalProps> = ({
  template,
  isSubmitting,
  onConfirm,
  onCancel,
}) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center mb-4">
          <Trash2 className="w-6 h-6 text-red-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Delete Template</h3>
        </div>

        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{template.name}</strong>? This action cannot be
          undone.
        </p>

        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
          <div className="flex items-start">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p>
                This template has been used <strong>{template.usageCount}</strong> time(s). Deleting
                it will not affect sites already created from it, but it will no longer be available
                for new sites or for Roary to use.
              </p>
            </div>
          </div>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={() => onConfirm(template.id)}
            disabled={isSubmitting}
            className={cn('btn-danger flex-1', isSubmitting && 'opacity-50 cursor-not-allowed')}
          >
            {isSubmitting ? 'Deleting...' : 'Delete Template'}
          </button>
          <button onClick={onCancel} disabled={isSubmitting} className="btn-secondary flex-1">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
